import { canonicalFileSchema } from "@/lib/data/schemas/files";
import { StateSchema } from "@/lib/data/schemas/CommonSchemaFields";
import { ConnectionSchema } from "@/lib/data/schemas/connections/ConnectionSchema";
import { ContactSchema } from "@/lib/data/schemas/contact/ContactSchema";
import {
  EmbeddedUserSchema,
  UserSchema,
} from "@/lib/data/schemas/firm/FirmSchema";
import { CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS } from "@/lib/data/static/contactStatics";
import { MATTER_INCIDENT_REPORT_SENTIMENT_OPTIONS } from "@/lib/data/static/fileStatics";
import { MATTER_EXPENSE_PAID_STATUS_OPTIONS } from "@/lib/data/static/matters/expenseStatics";
import { MATTER_INTAKE_TEMPLATE_QUESTION_TYPE_OPTIONS } from "@/lib/data/static/matters/intakeQuestionStatics";
import {
  MATTER_LIEN_CATEGORY_OPTIONS,
  MATTER_LIEN_SUBCATEGORY_OPTIONS,
} from "@/lib/data/static/matters/lienStatics";
import { MATTER_TASK_PRIORITY_OPTIONS } from "@/lib/data/static/matters/taskStatics";
import { MATTER_TRUST_CHECK_TYPE_OPTIONS } from "@/lib/data/static/matters/trustCheckStatics";
import {
  MATTER_CONTACT_CONNECTION_LABEL_OPTIONS,
  MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS,
  MATTER_INCIDENT_REPORT_STATUS_OPTIONS,
  MATTER_TASK_STATUS_OPTIONS,
  MATTER_TEMPLATE_CATEGORY_OPTIONS,
  MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS,
  MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS,
  MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS,
  MEDICAL_TREATMENT_MISSED_REASONS,
  MEDICAL_TREATMENT_RECORD_STATUS_OPTIONS,
} from "@/lib/data/static/matterStatics";
import { MATTER_FILE_TAG_VALUES } from "@/lib/utils/matterFile";
import dayjs from "dayjs";
import { z } from "zod";
import {
  MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS,
  MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS,
  NOT_APPLICABLE_INSURANCE_COVERAGE,
} from "../../static/matters/insuranceCoverageStatics";

const commonMatterFields = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  matterId: z.string(),
});

const matterSettingFields = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  firmId: z.string(),
  id: z.string(),
});

const aiAffectedFieldSchema = z.object({
  key: z.string(),
  oldValue: z.unknown().optional().nullable(),
  newValue: z.unknown().optional().nullable(),
});

export const IncidentTypeCategorySchema = z.object({
  dateCreated: z.string().nullish(),
  dateModified: z.string().nullish(),
  description: z.string().nullish(),
  id: z.string(),
  label: z.string(),
});

export const ClaimTypeSchema = IncidentTypeCategorySchema;

export const IncidentTypeSchema = z
  .object({
    incidentTypeCategoryId: z.string().nullable().optional(),
    category: IncidentTypeCategorySchema.nullish(),
    emailModuleAccess: z.boolean().nullable().optional(),
    expenseModuleAccess: z.boolean().nullable().optional(),
    incidentModuleAccess: z.boolean().nullable().optional(),
    incidentReportModuleAccess: z.boolean().nullable().optional(),
    injuriesModuleAccess: z.boolean().nullable().optional(),
    insuranceModuleAccess: z.boolean().nullable().optional(),
    intakePropertyDamageModuleAccess: z.boolean().nullable().optional(),
    intakeResponsesModuleAccess: z.boolean().nullable().optional(),
    lienModuleAccess: z.boolean().nullable().optional(),
    litigationModuleAccess: z.boolean().nullable().optional(),
    lostWagesModuleAccess: z.boolean().nullable().optional(),
    medicalModuleAccess: z.boolean().nullable().optional(),
    medicalTreatmentModuleAccess: z.boolean().nullable().optional(),
    settlementModuleAccess: z.boolean().nullable().optional(),
    signatureRequestModuleAccess: z.boolean().nullable().optional(),
    trustCheckModuleAccess: z.boolean().nullable().optional(),
    vehicleModuleAccess: z.boolean().nullable().optional(),
    description: z.string().nullable().optional(),
    label: z.string().nullable(),
  })
  .merge(
    matterSettingFields.extend({
      dateCreated: matterSettingFields.shape.dateCreated.nullable().optional(),
      dateModified: matterSettingFields.shape.dateModified
        .nullable()
        .optional(),
    }),
  );

export const MatterTagSchema = z
  .object({
    color: z.string(),
    description: z.string().nullable().optional(),
    label: z.string().trim(),
  })
  .merge(matterSettingFields);

export const MatterTagConnectionSchema = z.object({
  tag: MatterTagSchema,
  matterId: z.string(),
  firmId: z.string(),
  tagId: z.string(),
  id: z.string(),
});

export const MatterStatusCategorySchema = z.object({
  dateCreated: z.string().nullish(),
  dateModified: z.string().nullish(),
  description: z.string().nullish(),
  id: z.string(),
  label: z.string(),
});

export const MatterStatusSchema = z
  .object({
    category: MatterStatusCategorySchema,
    categoryId: z.string().min(1, { message: "Category is required" }),
    defaultAttorneyFee: z.number().nullable().optional(),
    description: z.string().nullable().optional(),
    goal: z.number().nullable().optional(),
    label: z.string().min(1, { message: "Label is required" }),
    order: z.number().nullable().optional(),
  })
  .merge(matterSettingFields);

export const RecentMatterSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  id: z.string(),
  lastSeenAt: z.string(),
  status: z.object({
    categoryId: z.string(),
    id: z.string(),
    label: z.string(),
  }),
  title: z.string(),
  incidentType: z
    .object({
      incidentTypeCategoryId: z.string().nullish(),
      id: z.string(),
      label: z.string().nullable(),
    })
    .nullish(),
});

export const MatterRoleSchema = z
  .object({
    description: z.string().nullable().optional(),
    label: z.string().min(1, { message: "Label is required" }),
    id: z.string(),
  })
  .merge(matterSettingFields);

export const MatterAssigneeSchema = z
  .object({
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    dateTimeEnd: z.string().optional(),
    dateTimeStart: z.string().optional(),
    description: z.string().trim().optional(),
    id: z.string().min(1, { message: "Assignee is required" }),
    primaryContact: z.boolean().default(false),
    teamConnectionId: z.string().nullable(),
  })
  .extend({ user: UserSchema })
  .extend({ role: MatterRoleSchema });

export const MatterTeamSchema = z
  .object({
    description: z.string().nullable().optional(),
    label: z.string().min(1, { message: "Label is required" }),
  })
  .merge(matterSettingFields);

export const MatterTeammateSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  firmId: z.string(),
  id: z.string(),
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  middleName: z.string().nullish(),
  email: z.string().optional(),
  phoneNumber: z.string().nullish(),
  username: z.string().nullish(),
  owner: z.boolean().nullish(),
  status: z.string().nullish(),
  roleId: z.string(),
  roleLabel: z.string(),
  roleDescription: z.string().nullish(),
  teamId: z.string(),
});

export const MatterTeamConnectionSchema = z
  .object({
    matterId: z.string().min(1, { message: "Matter is required" }),
    teamId: z.string().nullish(),
    dateTimeEnd: z.string().optional(),
    dateTimeStart: z.string().optional(),
    id: z.string().min(1, { message: "Connection is required" }),
    teammates: z.array(MatterTeammateSchema).optional(),
  })
  .merge(MatterTeamSchema);

export const MatterUserConnectionSchema = z.object({
  integrationAppId: z.string().nullish(),
  integrationAppName: z.string().nullish(),
  dateCreated: z.string(),
  dateModified: z.string(),
  email: z.string().nullable(),
  fileId: z.string().nullable(),
  firmId: z.string(),
  firstName: z.string().nullable(),
  id: z.string().min(1, { message: "Connection is required" }),
  label: z.string().nullable(),
  lastName: z.string().nullable(),
  litigationDateId: z.string().nullable(),
  matterId: z.string().min(1, { message: "Matter is required" }),
  medicalTreatmentBalanceConfirmationId: z.string().nullable(),
  middleName: z.string().nullable(),
  owner: z.boolean().nullable(),
  phoneNumber: z.string().nullable(),
  signatureRequestComplete: z.boolean().nullable(),
  signatureRequestId: z.string().nullable(),
  signatureRequestPasscode: z.string().nullable(),
  statuteOfLimitationsId: z.string().nullable(),
  status: z.string().nullable(),
  taskId: z.string().nullable(),
  userId: z.string().nullable(),
  username: z.string().nullable(),
  vehicleId: z.string().nullable(),
});

export const PinnedMatterSchema = z.object({
  dateCreated: z.string(),
  dateModified: z.string(),
  firmId: z.string(),
  id: z.string().min(1, { message: "Pinned matter is required" }),
  userId: z.string().min(1, { message: "User is required" }),
});

export const DashboardPinnedMatterSchema = PinnedMatterSchema.extend({
  matterId: z.string().min(1, { message: "Matter is required" }),
  matter: z.object({
    id: z.string().min(1, { message: "Matter is required" }),
    title: z.string().nullable(),
    clients: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        type: z.string(),
      }),
    ),
    status: z.object({
      id: z.string(),
      label: z.string(),
    }),
    incidentType: z
      .object({
        id: z.string(),
        label: z.string().nullable(),
      })
      .nullish(),
  }),
});

export const MatterStatusPhaseSchema = z
  .object({
    dateTimeEnd: z.string().optional(),
    dateTimeStart: z.string(),
    id: z.string(),
    statusId: z.string(),
  })
  .extend({
    status: MatterStatusSchema,
  });

export const MatterContactConnectionSchema = z.object({
  contact: ContactSchema,
  contactId: z.string(),
  dateCreated: z.string(),
  dateModified: z.string(),
  dateTimeEnd: z.string().nullable(),
  dateTimeStart: z.string().nullable(),
  description: z.string().nullable(),
  expenseId: z.string().nullable(),
  faultStatus: z
    .enum(MATTER_CONTACT_CONNECTION_FAULT_STATUS_OPTIONS)
    .nullable()
    .optional(),
  firmId: z.string(),
  id: z.string().min(1, { message: "Connection is required" }),
  incidentReportId: z.string().nullable(),
  insuranceCoverage: z
    .enum(MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS)
    .or(z.literal(NOT_APPLICABLE_INSURANCE_COVERAGE))
    .nullable(),
  insuranceId: z.string().nullable(),
  label: z
    .enum(MATTER_CONTACT_CONNECTION_LABEL_OPTIONS)
    .readonly()
    .refine((value) => Boolean(value), {
      message: "Label is required",
    }),
  lienId: z.string().nullable(),
  litigationDateId: z.string().nullable(),
  lostWagesId: z.string().nullable(),
  matterId: z.string(),
  medicalTreatmentAppointmentId: z.string().nullable(),
  medicalTreatmentBalanceConfirmationId: z.string().nullable(),
  medicalTreatmentBillId: z.string().nullable(),
  medicalTreatmentId: z.string().nullable(),
  medicalTreatmentPlanId: z.string().nullable(),
  medicalTreatmentRecordId: z.string().nullable(),
  propertyDamageId: z.string().nullable(),
  settlementCounterId: z.string().nullable(),
  settlementDemandId: z.string().nullable(),
  settlementOfferId: z.string().nullable(),
  shareFolderId: z.string().nullable(),
  shareFolderPasscode: z.string().nullable(),
  signatureRequestComplete: z.boolean().nullable(),
  signatureRequestId: z.string().nullable(),
  signatureRequestPasscode: z.string().nullable(),
  statuteOfLimitationsId: z.string().nullable(),
  surveyId: z.string().nullable().optional(),
  trustCheckId: z.string().nullable(),
  vehicleId: z.string().nullable(),
  witnessStatus: z
    .enum(MATTER_CONTACT_CONNECTION_WITNESS_STATUS_OPTIONS)
    .nullable()
    .optional(),
});

export const ContactMatterSummarySchema = z.object({
  connections: ConnectionSchema.array(),
  connectionsNextCursor: z.string().nullable().optional(),
  id: z.string(),
  title: z.string(),
});

export const MatterIncidentReportDataSchema = z
  .object({
    id: z.string(),
    description: z.string().optional(),
    fileNumber: z.string().trim().nullable().optional(),
    noReport: z.boolean().optional(),
    notApplicable: z.boolean().optional(),
    sentiment: z
      .enum(MATTER_INCIDENT_REPORT_SENTIMENT_OPTIONS)
      .or(z.literal(""))
      .nullable()
      .optional(),
    status: z
      .enum(MATTER_INCIDENT_REPORT_STATUS_OPTIONS)
      .or(z.literal(""))
      .optional(),
    time: z.string().nullable().optional(),
  })
  .merge(commonMatterFields);

export const MatterIncidentReportSchema = MatterIncidentReportDataSchema.extend(
  {
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  },
);

const preprocessCurrencyValue = (value: unknown) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? value : parsedValue;
  }
  return value;
};

const optionalCurrencySchema = z.preprocess(
  preprocessCurrencyValue,
  z.number().nullable(),
);

const optionalCurrencySchemaWithMax = (max: number, message: string) =>
  z.preprocess(
    preprocessCurrencyValue,
    z.number().max(max, { message }).nullable(),
  );

export const MatterVehicleDataSchema = z
  .object({
    id: z.string().min(1, { message: "Vehicle is required" }),
    make: z
      .string()
      .trim()
      .min(1, { message: "Make is required" })
      .max(30, "Make must be 30 characters or less"),
    model: z
      .string()
      .trim()
      .max(30, "Model must be 30 characters or less")
      .optional()
      .nullable()
      .or(z.literal("")),
    year: z
      .preprocess(
        (value) => {
          if (value === "" || value === null || value === undefined) {
            return null;
          }
          if (typeof value === "string") {
            const parsedValue = Number(value);
            return Number.isNaN(parsedValue) ? null : parsedValue;
          }
          return value;
        },
        z.union([
          z.coerce
            .number()
            .min(1900, { message: "Year must be greater than 1900" })
            .max(dayjs().add(1, "year").year(), {
              message: `Year cannot be greater than ${dayjs().add(1, "year").year()}`,
            }),
          z.literal(null),
        ]),
      )
      .optional(),
    color: z
      .string()
      .max(50, "Color must be 50 characters or less")
      .nullable()
      .optional(),
    damageSeverity: z
      .enum(MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS)
      .readonly()
      .optional()
      .nullable(),
    description: z
      .string()
      .max(5000, "Description must be 5,000 characters or less")
      .nullable()
      .optional(),
    licensePlateNumber: z
      .string()
      .max(50, "License plate must be 50 characters or less")
      .nullable()
      .optional(),
    registrationState: z.string().trim().nullable().optional(),
    repairEstimate: optionalCurrencySchemaWithMax(
      1000000000,
      "Repair estimate must be 1,000,000,000 or less",
    ),
    vin: z
      .string()
      .max(50, "VIN must be 50 characters or less")
      .nullable()
      .optional(),
    mileage: z.coerce
      .number()
      .positive()
      .int({ message: "Mileage must be an integer" })
      .max(1000000000, { message: "Mileage must be 1,000,000,000 or less" })
      .nullable()
      .optional(),
    totalLoss: z.coerce
      .number()
      .max(1000000000, {
        message: "Total loss must be 1,000,000,000 or less",
      })
      .nullable()
      .optional(),
    insuranceId: z.string().nullable().optional(),
    deductiblePaidAmount: z.coerce
      .number()
      .max(1000000000, {
        message: "Deductible paid amount must be 1,000,000,000 or less",
      })
      .nullable()
      .optional(),
    deductibleReimbursed: z.boolean().nullable().optional(),
    lossOfUseAmount: z.coerce
      .number()
      .max(1000000000, {
        message: "Loss of use amount must be 1,000,000,000 or less",
      })
      .nullable()
      .optional(),
    lossOfUse: z.boolean().nullable().optional(),
    lossOfUseDetails: z
      .string()
      .max(5000, "Loss of use details must be 5,000 characters or less")
      .nullable()
      .optional(),
    lossOfUseReimbursed: z.boolean().nullable().optional(),
  })
  .merge(commonMatterFields);

export const MatterVehicleSchema = MatterVehicleDataSchema.extend({
  connections: ConnectionSchema.array().default([]),
  connectionsNextCursor: z.string().nullable().optional(),
});

export const MatterEventSchema = z
  .object({
    actionType: z.string().nullable().optional(),
    actor: z
      .object({
        firstName: z.string().nullable().optional(),
        id: z.string(),
        lastName: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    actorUserId: z.string().nullable().optional(),
    eventMetadata: z.record(z.unknown()).nullable().optional(),
    changes: z
      .record(
        z.object({
          from: z.unknown().optional(),
          to: z.unknown().optional(),
        }),
      )
      .nullable()
      .optional(),
    id: z.string(),
    message: z.string().nullable().optional(),
    primarySubjectId: z.string().nullable().optional(),
    primarySubjectType: z.string().nullable().optional(),
    subjects: z
      .array(
        z
          .object({
            id: z.string(),
            type: z.string(),
          })
          .passthrough(),
      )
      .optional(),
  })
  .merge(commonMatterFields);

export const MATTER_EMAIL_DELIVERY_STATUS_OPTIONS = [
  "PENDING",
  "SENT",
  "FAILED",
] as const;

export const MatterEmailSchema = z.object({
  id: z.string(),
  matterId: z.string(),
  firmId: z.string(),
  subject: z.string().trim().nullish(),
  fromEmails: z.array(z.string()).nullish(),
  toEmails: z.array(z.string()).nullish(),
  ccEmails: z.array(z.string()).nullish(),
  bccEmails: z.array(z.string()).nullish(),
  deliveryStatus: z.enum(MATTER_EMAIL_DELIVERY_STATUS_OPTIONS),
  presignedUrlKey: z.string().nullish(),
  dateCreated: z.string().nullish(),
  dateModified: z.string().nullish(),
});

export const MatterEmailAttachmentSchema = z
  .object({
    id: z.string(),
    emailId: z.string(),
    name: z.string().trim().nullish(),
    presignedUrlKey: z.string().nullish(),
  })
  .merge(commonMatterFields);

export const MatterTaskSchema = z
  .object({
    phaseActionId: z.string().nullish(),
    phaseAction: z
      .object({
        id: z.string(),
        title: z.string(),
        phaseId: z.string(),
        phaseTitle: z.string(),
        actionDefinitionId: z.string(),
        isCurrent: z.boolean(),
      })
      .nullish(),
    id: z.string(),
    title: z
      .string()
      .trim()
      .min(1, { message: "Title is required" })
      .max(100, "Title must be 100 characters or less"),
    description: z
      .string()
      .max(5000, "Description must be 5,000 characters or less")
      .nullable()
      .optional(),
    status: z
      .enum(MATTER_TASK_STATUS_OPTIONS)
      .readonly()
      .or(z.literal(""))
      .refine((value) => value, {
        message: "Status is required",
      }),
    priority: z.enum(MATTER_TASK_PRIORITY_OPTIONS).optional().nullable(),
    startDate: z.string().nullish(),
    dueDate: z.string().nullish(),
    completedDate: z.string().nullish(),
    vehicleId: z.string().optional(),
    insuranceId: z.string().optional(),
    medicalTreatmentId: z.string().optional(),
    medicalTreatmentAppointmentId: z.string().optional(),
    medicalTreatmentBillId: z.string().optional(),
    medicalTreatmentRecordId: z.string().optional(),
    medicalTreatmentPlanId: z.string().optional(),
    medicalTreatmentBalanceConfirmationId: z.string().optional(),
    settlementDemandId: z.string().optional(),
    settlementOfferId: z.string().optional(),
    settlementCounterId: z.string().optional(),
    expenseId: z.string().optional(),
    lostWagesId: z.string().optional(),
    lienId: z.string().optional(),
    propertyDamageId: z.string().optional(),
    signatureRequestId: z.string().optional(),
    trustCheckId: z.string().optional(),
    incidentReportId: z.string().optional(),
    litigationDateId: z.string().optional(),
    emailId: z.string().optional(),
    fileId: z.string().optional(),
    shareFolderId: z.string().optional(),
    statuteOfLimitationsId: z.string().optional(),
  })
  .extend({
    user: EmbeddedUserSchema.nullish(),
  })
  .merge(commonMatterFields);

export const MatterTaskCommentSchema = z
  .object({
    mentionedApps: z
      .array(
        z.object({
          integrationAppId: z.string(),
          integrationAppName: z.string().nullable(),
        }),
      )
      .optional(),
    taskId: z.string(),
    id: z.string(),
    draft: z.boolean().optional(),
    message: z
      .string()
      .trim()
      .min(1, { message: "Message is required" })
      .max(5000, "Message must be 5,000 characters or less"),
    publishedDate: z.string().nullable().optional(),
    integrationAppId: z.string().nullable().optional(),
    integrationAppName: z.string().nullable().optional(),
    userId: z.string().nullable(),
  })
  .merge(commonMatterFields);

export const MatterNoteSchema = z.object({
  mentionedApps: z
    .array(
      z.object({
        integrationAppId: z.string(),
        integrationAppName: z.string().nullable(),
      }),
    )
    .optional(),
  emailId: z.string().nullable(),
  expenseId: z.string().nullable(),
  fileId: z.string().nullable(),
  incidentReportId: z.string().nullable(),
  insuranceId: z.string().nullable(),
  lienId: z.string().nullable(),
  litigationDateId: z.string().nullable(),
  lostWagesId: z.string().nullable(),
  medicalTreatmentAppointmentId: z.string().nullable(),
  medicalTreatmentBalanceConfirmationId: z.string().nullable(),
  medicalTreatmentBillId: z.string().nullable(),
  medicalTreatmentId: z.string().nullable(),
  medicalTreatmentPlanId: z.string().nullable(),
  medicalTreatmentRecordId: z.string().nullable(),
  propertyDamageId: z.string().nullable(),
  settlementCounterId: z.string().nullable(),
  settlementDemandId: z.string().nullable(),
  settlementOfferId: z.string().nullable(),
  shareFolderId: z.string().nullable(),
  signatureRequestId: z.string().nullable(),
  statuteOfLimitationsId: z.string().nullable(),
  trustCheckId: z.string().nullable(),
  vehicleId: z.string().nullable(),
  dateCreated: z.string(),
  dateModified: z.string(),
  firmId: z.string(),
  massTortId: z.string().nullable(),
  matterId: z.string(),
  draft: z.boolean(),
  id: z.string(),
  integrationAppId: z.string().nullable().optional(),
  integrationAppName: z.string().nullable().optional(),
  message: z
    .string()
    .trim()
    .min(1, { message: "Message is required" })
    .max(5000, "Message must be 5,000 characters or less"),
  pinned: z.boolean(),
  publishedDate: z.string(),
  userId: z.string().nullable(),
});

export const MatterNoteCommentSchema = z.object({
  mentionedApps: z
    .array(
      z.object({
        integrationAppId: z.string(),
        integrationAppName: z.string().nullable(),
      }),
    )
    .optional(),
  emailId: z.string().nullable(),
  expenseId: z.string().nullable(),
  fileId: z.string().nullable(),
  incidentReportId: z.string().nullable(),
  insuranceId: z.string().nullable(),
  lienId: z.string().nullable(),
  litigationDateId: z.string().nullable(),
  lostWagesId: z.string().nullable(),
  medicalTreatmentAppointmentId: z.string().nullable(),
  medicalTreatmentBalanceConfirmationId: z.string().nullable(),
  medicalTreatmentBillId: z.string().nullable(),
  medicalTreatmentId: z.string().nullable(),
  medicalTreatmentPlanId: z.string().nullable(),
  medicalTreatmentRecordId: z.string().nullable(),
  propertyDamageId: z.string().nullable(),
  settlementCounterId: z.string().nullable(),
  settlementDemandId: z.string().nullable(),
  settlementOfferId: z.string().nullable(),
  shareFolderId: z.string().nullable(),
  signatureRequestId: z.string().nullable(),
  statuteOfLimitationsId: z.string().nullable(),
  trustCheckId: z.string().nullable(),
  vehicleId: z.string().nullable(),
  dateCreated: z.string(),
  dateModified: z.string(),
  firmId: z.string(),
  massTortId: z.string().nullable(),
  matterId: z.string(),
  draft: z.boolean(),
  id: z.string(),
  message: z
    .string()
    .trim()
    .min(1, { message: "Message is required" })
    .max(5000, "Message must be 5,000 characters or less"),
  noteId: z.string(),
  publishedDate: z.string(),
  userId: z.string(),
});

export const DefaultMatterFileRepositoryStructureSchema = z
  .object({
    description: z.string().nullable().optional(),
    label: z.string().min(1, { message: "Label is required" }),
    incidentTypeIds: z.array(z.string()).nullable().optional(),
  })
  .merge(matterSettingFields);

export const DefaultMatterFileRepositoryStructureFolderSchema = z.object({
  id: z.string(),
  structureId: z.string(),
  firmId: z.string(),
  name: z
    .string()
    .trim()
    .min(1, { message: "Name is required" })
    .max(100, { message: "Name must be 100 characters or less" }),
  description: z
    .string()
    .max(5000, { message: "Description must be 5,000 characters or less" })
    .nullable()
    .optional(),
  idLocation: z.string().nullable(),
  dateCreated: z.string().nullable().optional(),
  dateModified: z.string().nullable().optional(),
});

export const DefaultMatterExpenseSchema = z
  .object({
    category: z.string().trim().min(1, { message: "Category is required" }),
    cost: z
      .preprocess(
        (value) => (value === "" ? null : value),
        z.coerce.number().nullable(),
      )
      .superRefine((value, context) => {
        if (value === null) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Cost is required",
          });
        }
      }),
    description: z.string().nullable().optional(),
    incidentState: z.array(StateSchema).nullable().optional(),
    label: z.string().min(1, {
      message: "Label is required",
    }),
    incidentTypeIds: z.array(z.string()).min(1, {
      message: "Incident type is required",
    }),
  })
  .merge(matterSettingFields);

export const MatterFileSchema = z
  .object({
    matterId: z.string(),
    id: z.string(),
    description: z
      .string()
      .trim()
      .max(5000, "Description must be 5,000 characters or less")
      .nullable()
      .optional(),
    isFolder: z.boolean().optional(),
    tags: z.array(z.enum(MATTER_FILE_TAG_VALUES)).nullable().optional(),
    idLocation: z.string().nullable(),
    name: z
      .string()
      .trim()
      .min(1, { message: "Name is required" })
      .max(100, "Name must be 100 characters or less")
      .refine((value) => !value.includes("/"), {
        message: "cannot contain forward slashes (/)",
      }),
    presignedUrlKey: z.string().nullish(),
    variables: z.array(z.string()).nullish(),
  })
  .merge(commonMatterFields)
  .merge(
    canonicalFileSchema
      .pick({
        revision: true,
        currentVersionId: true,
        currentVersionNumber: true,
        hasPreviousVersions: true,
        capabilities: true,
        contentType: true,
        sizeBytes: true,
      })
      .partial(),
  );

export const DuplicateMatterFileSchema = z.object({
  matterId: z.string(),
  id: z.string(),
  name: MatterFileSchema.shape.name,
  idLocation: z.string().optional(),
});

export const MatterSignatureRequestSchema = z
  .object({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
    signerAccess: z.array(MatterContactConnectionSchema).default([]),
    matterSignatureRequestId: z.string(),
    matterSignatureRequestFileIdDestination: z.string(),
    matterSignatureRequestExpirationDate: z
      .string()
      .date()
      .refine((value) => value, {
        message: "Expiration date is required",
      })
      .refine(
        (value) => {
          const selected = dayjs(value).startOf("day");
          const today = dayjs().startOf("day");
          return selected.isValid() && !selected.isBefore(today);
        },
        {
          message: "Expiration date cannot be in the past",
        },
      ),
    matterId: z.string().min(1, { message: "Matter is required" }),
    matterFileId: z.string().min(1, { message: "File is required" }),
    matterContactConnections: z.array(
      z.object({
        label: MatterContactConnectionSchema.shape.label,
        matterContactConnectionContact: ContactSchema,
        matterSignatureRequestSignerOrder: z.number().optional(),
      }),
    ),
    matterUserConnections: z
      .array(
        z.object({
          matterUserConnectionLabel: z.string().min(1),
          matterUserConnectionUser: UserSchema,
          matterSignatureRequestSignerOrder: z.number().optional(),
        }),
      )
      .optional(),
    matterSignatureRequestFields: z
      .array(
        z.object({
          matterSignatureRequestFieldHeight: z.number(),
          matterSignatureRequestFieldLabel: z.string().optional(),
          matterSignatureRequestFieldPage: z.number(),
          matterSignatureRequestFieldRequired: z.boolean().optional(),
          matterSignatureRequestFieldSignerId: z.string(),
          matterSignatureRequestFieldSignerType: z.enum(["contact", "user"]),
          matterSignatureRequestFieldType: z.enum([
            "signature",
            "initials",
            "date",
            "text",
            "checkbox",
          ]),
          matterSignatureRequestFieldWidth: z.number(),
          matterSignatureRequestFieldX: z.number(),
          matterSignatureRequestFieldY: z.number(),
        }),
      )
      .optional(),
    matterFileName: MatterFileSchema.shape.name,
    matterFilePresignedUrlKey: MatterFileSchema.shape.presignedUrlKey,
  })
  .merge(commonMatterFields);

export const MatterFileReferenceSchema = z.object({
  fileId: z.string(),
  id: z.string(),
  matterId: z.string(),
  vehicleId: z.string().optional(),
  insuranceId: z.string().optional(),
  emailId: z.string().optional(),
  expenseId: z.string().optional(),
  incidentReportId: z.string().optional(),
  litigationDateId: z.string().optional(),
  medicalTreatmentId: z.string().optional(),
  medicalTreatmentAppointmentId: z.string().optional(),
  medicalTreatmentBillId: z.string().optional(),
  medicalTreatmentPlanId: z.string().optional(),
  medicalTreatmentRecordId: z.string().optional(),
  medicalTreatmentBalanceConfirmationId: z.string().optional(),
  settlementDemandId: z.string().optional(),
  settlementOfferId: z.string().optional(),
  settlementCounterId: z.string().optional(),
  lostWagesId: z.string().optional(),
  lienId: z.string().optional(),
  propertyDamageId: z.string().optional(),
  signatureRequestId: z.string().optional(),
  trustCheckId: z.string().optional(),
  shareFolderId: z.string().optional(),
  statuteOfLimitationsId: z.string().optional(),
});

export const MatterShareFolderSchema = z
  .object({
    id: z.string(),
    description: z.string().nullable(),
    expirationDate: z
      .string()
      .date()
      .refine((value) => value, { message: "Expiration date is required" }),
    label: z
      .string()
      .trim()
      .min(1, { message: "Label is required" })
      .max(100, "Label must be 100 characters or less"),
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
    recipientAccess: z.array(MatterContactConnectionSchema).default([]),
    firmId: z.string(),
  })
  .merge(commonMatterFields);

export const MatterShareFolderFileSchema = z
  .object({
    id: z.string(),
    fileId: z.string(),
    shareFolderId: z.string().nullable(),
    firmId: z.string(),
    matterId: z.string(),
    presignedUrlKey: z.string().nullable(),
    dateCreated: z.string(),
    dateModified: z.string(),
  })
  .strict();

export const MatterTemplateSchema = z
  .object({
    category: z
      .array(z.enum(MATTER_TEMPLATE_CATEGORY_OPTIONS))
      .nullable()
      .optional(),
    description: z.string().nullable().optional(),
    moduleShortcut: z.array(z.string()).nullable().optional(),
    name: z.string().min(1, { message: "Name is required" }),
    presignedUrlKey: z.string(),
    type: z.string().nullable().optional(),
    incidentTypeIds: z.array(z.string()).nullable().optional(),
    variables: z.array(z.string()).nullable().optional(),
  })
  .merge(matterSettingFields);

export const MatterIntakeTemplateSchema = z
  .object({
    description: z.string().nullable().optional(),
    label: z.string().min(1, { message: "Label is required" }),
  })
  .merge(matterSettingFields);

export const MatterIntakeTemplateQuestionSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  firmId: z.string(),
  label: z
    .string()
    .trim()
    .min(1, {
      message: "Question is required",
    })
    .max(100, { message: "Question must be 100 characters or less" }),
  options: z.unknown().nullable().optional(),
  order: z.coerce
    .number({
      message: "Must be a number",
    })
    .max(1_000_000_000, { message: "Order must be 1,000,000,000 or less" })
    .nullable()
    .optional(),
  type: z.enum(MATTER_INTAKE_TEMPLATE_QUESTION_TYPE_OPTIONS),
  dateCreated: z.string().nullable().optional(),
  dateModified: z.string().nullable().optional(),
});

export const MatterIntakeTemplateResponseSchema = z.object({
  id: z.string(),
  questionId: z.string(),
  matterId: z.string(),
  firmId: z.string(),
  answer: z.string().nullable().optional(),
  dateCreated: z.string().nullable().optional(),
  dateModified: z.string().nullable().optional(),
  question: MatterIntakeTemplateQuestionSchema.nullish(),
});

export const MatterInsuranceDataSchema = z
  .object({
    category: z.string().optional(),
    claimNumber: z
      .string()
      .max(30, "Claim number must be 30 characters or less")
      .optional(),
    coverage: z
      .enum(MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS)
      .optional()
      .nullable()
      .or(z.literal("")),
    coverageEnabled: z.boolean().optional(),
    groupNumber: z.string().optional(),
    id: z.string(),
    liability: z.string().nullable().optional(),
    liabilityPercentage: z.coerce
      .number()
      .min(0, { message: "Percentage must be between 0 and 100" })
      .max(100, { message: "Percentage must be between 0 and 100" })
      .nullable()
      .or(z.literal(null))
      .optional(),
    medPay: z.boolean().optional(),
    medPayLimit: z.coerce.number().nullable().or(z.literal(null)).optional(),
    memberNumber: z.string().optional(),
    policyLimitConfirmation: z.boolean().optional(),
    policyLimitPerIncident: z.coerce
      .number()
      .nullable()
      .or(z.literal(null))
      .optional(),
    policyLimitPerPerson: z.coerce
      .number()
      .nullable()
      .or(z.literal(null))
      .optional(),
    policyNumber: z
      .string()
      .max(30, "Policy number must be 30 characters or less")
      .optional(),
    subrogation: z.boolean().optional(),
    subrogationFileNumber: z
      .string()
      .max(30, "Subrogation file number must be 30 characters or less")
      .optional(),
    type: z.string(),
    uimExists: z.boolean().optional(),
    uimPerIncident: z.coerce.number().nullable().or(z.literal(null)).optional(),
    uimPerPerson: z.coerce.number().nullable().or(z.literal(null)).optional(),
    umExists: z.boolean().optional(),
    umPerIncident: z.coerce.number().nullable().or(z.literal(null)).optional(),
    umPerPerson: z.coerce.number().nullable().or(z.literal(null)).optional(),
    umbrellaPolicyLimit: z.coerce.number().nullable().optional(),
  })
  .merge(commonMatterFields);

export const MatterInsuranceSchema = MatterInsuranceDataSchema.extend({
  connections: ConnectionSchema.array().default([]),
  connectionsNextCursor: z.string().nullable().optional(),
});

export const MedicalTreatmentSchema = z
  .object({
    id: z.string(),
    type: z.array(
      z.enum(CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS),
    ),
    description: z
      .string()
      .max(5000, "Description must be 5,000 characters or less")
      .optional(),
    nextSteps: z.string().optional(),
    recommendations: z.string().optional(),
    procedures: z.string().optional(),
    status: z.string().optional(),
    portalLink: z.string().optional(),
    portalDetails: z
      .string()
      .max(5000, "Portal details must be 5,000 characters or less")
      .optional(),
    patientAccountNumber: z
      .string()
      .max(50, "Patient account number must be 50 characters or less")
      .optional(),
    letterOfProtection: z
      .enum(MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS)
      .nullable()
      .optional()
      .or(z.literal("")),
    lien: z
      .enum(MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS)
      .nullable()
      .optional()
      .or(z.literal("")),
    soap: z.string().optional(),
    billNotApplicable: z.boolean().optional(),
    planNotApplicable: z.boolean().optional(),
    recordNotApplicable: z.boolean().optional(),
  })
  .merge(commonMatterFields)
  .extend({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  });

export const MedicalTreatmentBalanceConfirmationSchema = z
  .object({
    id: z.string(),
    confirmed: z.boolean().nullable().optional(),
    date: z.string().optional(),
    amount: z.coerce.number().nullable().or(z.literal(null)).optional(),
    medicalTreatmentId: z.string().optional(),
  })
  .merge(commonMatterFields)
  .extend({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  });

export const MedicalTreatmentAppointmentSchema = z
  .object({
    id: z.string(),
    medicalTreatmentId: z.string(),
    date: z.string().min(1, {
      message: "Date is required",
    }),
    dateEnd: z.string().optional().nullable(),
    multiday: z.boolean().optional(),
    type: z
      .array(z.enum(CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS))
      .optional(),
    time: z.string().optional(),
    outcome: z
      .enum(["missed", "attended", "scheduled"])
      .optional()
      .nullable()
      .or(z.literal("")),
    missedReason: z
      .enum(MEDICAL_TREATMENT_MISSED_REASONS)
      .optional()
      .nullable()
      .or(z.literal("")),
    noShowReason: z.string().optional().nullable(),
    description: z
      .string()
      .max(5000, "Description must be 5,000 characters or less")
      .optional(),
    billNotApplicable: z.boolean().optional(),
    recordNotApplicable: z.boolean().optional(),
  })
  .merge(commonMatterFields)
  .extend({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  });

export const MedicalTreatmentBillSchema = z
  .object({
    aiAffectedFields: aiAffectedFieldSchema.array().optional(),
    aiStatus: z.string().optional(),
    id: z.string(),
    medicalTreatmentId: z.string(),
    adjustedBill: z.coerce
      .number()
      .max(1000000000, {
        message: "Adjusted bill must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    clientPaid: z.coerce
      .number()
      .max(1000000000, {
        message: "Client paid must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    description: z
      .string()
      .max(5000, "Description must be 5,000 characters or less")
      .optional(),
    fileNumber: z.coerce.number().nullable().or(z.literal(null)).optional(),
    goalAmount: z.coerce
      .number()
      .max(1000000000, {
        message: "Goal amount must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    insurancePaid: z.coerce
      .number()
      .max(1000000000, {
        message: "Insurance paid must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    letterOfProtection: z
      .enum(MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS)
      .nullable()
      .optional()
      .or(z.literal("")),
    lien: z
      .enum(MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS)
      .nullable()
      .optional()
      .or(z.literal("")),
    medpayPipPaid: z.coerce
      .number()
      .max(1000000000, {
        message: "MedPay/PIP paid must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    negotiatedAmount: z.coerce
      .number()
      .max(1000000000, {
        message: "Negotiated amount must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    originalBill: z.coerce
      .number()
      .max(1000000000, {
        message: "Original bill must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null)),
    recordReceivedDate: z.string().optional().nullable(),
    recordRequestDate: z.string().optional().nullable(),
    recordRejectedDate: z.string().optional().nullable(),
    recordRejectedReason: z.string().optional().nullable(),
    recordStatus: z
      .enum(MEDICAL_TREATMENT_RECORD_STATUS_OPTIONS)
      .or(z.literal(""))
      .refine((value) => value, { message: "Status is required" }),
    recordLabel: z.string().trim().max(50, {
      message: "Record label must be less than 50 characters",
    }),
    reductionFeeAgreement: z.boolean().optional(),
    remainingBalance: z.coerce
      .number()
      .max(1000000000, {
        message: "Remaining balance must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    status: z.string().optional(),
    totalPaid: z.coerce
      .number()
      .max(1000000000, {
        message: "Total paid must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    totalPaidPlusOwed: z.coerce
      .number()
      .max(1000000000, {
        message: "Total paid plus owed must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
  })
  .merge(commonMatterFields)
  .extend({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  });

export const MedicalTreatmentAppointmentConnectionSchema = z
  .object({
    medicalTreatmentAppointmentId: z.string(),
    medicalTreatmentBillId: z.string().optional(),
    medicalTreatmentPlanId: z.string().optional(),
    medicalTreatmentRecordId: z.string().optional(),
    id: z.string(),
    medicalTreatmentId: z.string(),
  })
  .merge(commonMatterFields);

export const MedicalTreatmentRecordSchema = z
  .object({
    medicalTreatmentId: z.string(),
    id: z.string(),
    name: z.string().trim().max(50, {
      message: "Record name must be less than 50 characters",
    }),
    status: z
      .enum(MEDICAL_TREATMENT_RECORD_STATUS_OPTIONS)
      .or(z.literal(""))
      .refine((value) => value, {
        message: "Status is required",
      }),
    requestDate: z.string().optional().nullable(),
    receivedDate: z.string().optional().nullable(),
    rejectedDate: z.string().optional().nullable(),
    rejectedReason: z.string().optional().nullable(),
  })
  .merge(commonMatterFields)
  .extend({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  });

export const MedicalTreatmentPlanSchema = z
  .object({
    id: z.string(),
    date: z.string().refine((value) => value, {
      message: "Date is required",
    }),
    status: z.string().refine((value) => value, {
      message: "Status is required",
    }),
    description: z
      .string()
      .max(5000, "Description must be 5,000 characters or less")
      .optional(),
    medicalTreatmentId: z.string().optional(),
  })
  .merge(commonMatterFields)
  .extend({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  });

export const MatterTaskPlanSchema = z
  .object({
    description: z.string().nullable().optional(),
    title: z.string().min(1, { message: "Title is required" }),
    incidentTypeIds: z.array(z.string()).nullable().optional(),
  })
  .merge(matterSettingFields);

export const MatterTaskPlanTaskSchema = z
  .object({
    taskPlanId: z.string().min(1, { message: "Task plan is required" }),
    title: z
      .string()
      .trim()
      .min(1, { message: "Title is required" })
      .max(200, { message: "Title must be less than 200 characters" }),
    description: z
      .string()
      .max(5000, { message: "Description must be 5,000 characters or less" })
      .nullable()
      .optional(),
    priority: z.enum(MATTER_TASK_PRIORITY_OPTIONS).nullable().optional(),
    startDate: z.coerce
      .number()
      .max(1_000_000_000, {
        message: "Start date must be 1,000,000,000 or less",
      })
      .nullable()
      .optional(),
    dueDate: z.coerce
      .number()
      .max(1_000_000_000, {
        message: "Due date must be 1,000,000,000 or less",
      })
      .nullable()
      .optional(),
    repeatable: z.boolean().nullable().optional(),
    triggerStatusId: z.string().nullable().optional(),
    triggerSubitem: z.string().nullable().optional(),
    triggerTaskId: z.string().nullable().optional(),
    assigneeRoleId: z.string().nullable().optional(),
    assigneeUserId: z.string().nullable().optional(),
    assigneeUserIds: z.array(z.string()).default([]),
    assigneeBackupUserId: z.string().nullable().optional(),
    assignerRoleId: z.string().nullable().optional(),
    assignerUserId: z.string().nullable().optional(),
    assignerUserIds: z.array(z.string()).default([]),
    assignerBackupUserId: z.string().nullable().optional(),
  })
  .merge(
    matterSettingFields.extend({
      dateCreated: matterSettingFields.shape.dateCreated.optional(),
      dateModified: matterSettingFields.shape.dateModified.optional(),
      firmId: matterSettingFields.shape.firmId.optional(),
    }),
  );

export const MatterTaskPlanTaskRoleSchema = z
  .object({
    matterRoleId: z.string(),
    matterRoleLabel: z.string(),
    matterTaskPlanId: z.string(),
    matterTaskPlanTaskId: z.string(),
    matterTaskPlanTaskRoleId: z.string(),
    matterTaskPlanTaskRoleLabel: z.string(),
  })
  .merge(commonMatterFields);

export const MatterSettlementDemandSchema = z
  .object({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
    id: z.string(),
    insuranceId: z.string().optional(),
    amount: optionalCurrencySchemaWithMax(
      1000000000,
      "Demand amount must be 1,000,000,000 or less",
    ).optional(),
    attorneyFeePercentage: z.coerce
      .number()
      .min(0, { message: "Percentage must be between 0 and 100" })
      .max(100, { message: "Percentage must be between 0 and 100" })
      .nullable()
      .or(z.literal(null))
      .optional(),
    attorneyFeeAmount: optionalCurrencySchemaWithMax(
      1000000000,
      "Attorney fee amount must be 1,000,000,000 or less",
    ).optional(),
    dateExpires: z.string().nullable().optional(),
    dateSent: z.string().min(1, { message: "Date sent is required" }),
    details: z
      .string()
      .trim()
      .max(5000, "Description must be 5,000 characters or less")
      .nullable()
      .optional(),
    coverageType: z.string().nullable().optional(),
    accepted: z.boolean().optional(),
    acceptedDate: z.string().nullable().optional(),
    acceptedWithIssues: z.boolean().nullable().optional(),
    medPayRefund: z.boolean().nullable().optional(),
    medPayRefundAmount: optionalCurrencySchemaWithMax(
      1000000000,
      "MedPay refund amount must be 1,000,000,000 or less",
    ).optional(),
    policyLimit: z.boolean().nullable().optional(),
  })
  .merge(commonMatterFields);

const MatterSettlementDemandPackageSegmentBaseSchema = z
  .object({
    id: z.string().nullable().optional(),
    settlementDemandId: z.string().optional(),
    order: z.coerce.number().nullable().or(z.literal(null)).optional(),
    fileId: z.string().trim().nullable().optional(),
    templateId: z.string().trim().nullable().optional(),
    templateInput: z.string().trim().nullable().optional(),
  })
  .merge(commonMatterFields);

export const MatterSettlementDemandPackageSegmentFormSchema =
  MatterSettlementDemandPackageSegmentBaseSchema;

export const MatterSettlementDemandPackageSegmentSchema =
  MatterSettlementDemandPackageSegmentBaseSchema.superRefine((data, ctx) => {
    const hasFileId = Boolean(data.fileId);
    const hasTemplateId = Boolean(data.templateId);
    const hasTemplateInput = Boolean(data.templateInput);
    const hasTemplateAndInput = hasTemplateId && hasTemplateInput;

    if (!hasFileId && !hasTemplateInput && !hasTemplateId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A file, template, or cover page description is required",
        path: ["templateInput"],
      });
    }

    if (hasFileId && (hasTemplateId || hasTemplateInput)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "A file cannot include template details",
        path: ["fileId"],
      });
      return;
    }

    if (hasTemplateAndInput) return;

    const selections = [hasFileId, hasTemplateId, hasTemplateInput].filter(
      Boolean,
    ).length;

    if (selections > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select only one: file, template, or cover page",
        path: ["fileId"],
      });
    }
  });

export const MatterSettlementOfferSchema = z
  .object({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
    id: z.string(),
    insuranceId: z.string().optional(),
    accepted: z.boolean().optional(),
    acceptedDate: z.string().nullable().optional(),
    acceptedWithIssues: z.boolean().optional(),
    status: z.string().trim().nullable().optional(),
    amount: optionalCurrencySchemaWithMax(
      1000000000,
      "Offer amount must be 1,000,000,000 or less",
    ).optional(),
    attorneyFeePercentage: z.coerce
      .number()
      .min(0, { message: "Percentage must be between 0 and 100" })
      .max(100, { message: "Percentage must be between 0 and 100" })
      .nullable()
      .or(z.literal(null))
      .optional(),
    attorneyFeeAmount: optionalCurrencySchemaWithMax(
      1000000000,
      "Attorney fee amount must be 1,000,000,000 or less",
    ).optional(),
    dateExpires: z.string().nullable().optional(),
    dateReceived: z.string().min(1, { message: "Date received is required" }),
    details: z
      .string()
      .trim()
      .max(5000, "Description must be 5,000 characters or less")
      .nullable()
      .optional(),
    coverageType: z.string().nullable().optional(),
    medPay: z.boolean().nullable().optional(),
    medPayRefund: z.boolean().nullable().optional(),
    medPayRefundAmount: optionalCurrencySchemaWithMax(
      1000000000,
      "MedPay refund amount must be 1,000,000,000 or less",
    ).optional(),
    policyLimit: z.boolean().nullable().optional(),
  })
  .merge(commonMatterFields);

export const MatterSettlementCounterSchema = z
  .object({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
    id: z.string(),
    insuranceId: z.string().optional(),
    accepted: z.boolean().optional(),
    acceptedDate: z.string().nullable().optional(),
    status: z.string().trim().nullable().optional(),
    acceptedDetails: z.string().trim().nullable().optional(),
    amount: optionalCurrencySchema.optional(),
    coverageType: z.string().nullable().optional(),
    attorneyFeePercentage: z.coerce
      .number()
      .min(0, { message: "Percentage must be between 0 and 100" })
      .max(100, { message: "Percentage must be between 0 and 100" })
      .nullable()
      .or(z.literal(null))
      .optional(),
    attorneyFeeAmount: optionalCurrencySchema.optional(),
    dateSent: z.string().min(1, { message: "Date sent is required" }),
    policyLimit: z.boolean().nullable().optional(),
  })
  .merge(commonMatterFields);

export const MatterExpenseSchema = z
  .object({
    id: z.string(),
    category: z
      .string()
      .min(1, { message: "Category is required" })
      .max(50, "Category must be 50 characters or less"),
    cost: z.coerce
      .number()
      .max(1000000000, { message: "Cost must be 1,000,000,000 or less" })
      .nullable()
      .or(z.literal(null))
      .refine((value) => value, {
        message: "Cost is required",
      }),
    dateIncurred: z.string().nullable().optional(),
    description: z
      .string()
      .trim()
      .max(5000, "Description must be 5,000 characters or less")
      .nullable()
      .optional(),
    label: z
      .string()
      .trim()
      .min(1, { message: "Label is required" })
      .max(50, "Label must be 50 characters or less"),
    paidByMethod: z.string().nullable().optional(),
    paidStatus: z
      .enum(MATTER_EXPENSE_PAID_STATUS_OPTIONS)
      .nullable()
      .optional()
      .or(z.literal("")),
    paidAmount: z.coerce
      .number()
      .max(1000000000, {
        message: "Paid amount must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    paidByMethodDetails: z
      .string()
      .trim()
      .max(50, "Payment method details must be 50 characters or less")
      .nullable()
      .optional(),
    refunded: z.boolean().nullable().optional(),
    refundedAmount: z.coerce
      .number()
      .max(1000000000, {
        message: "Refunded amount must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
    reducedAmount: z.coerce
      .number()
      .max(1000000000, {
        message: "Reduced amount must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional(),
  })
  .merge(commonMatterFields)
  .extend({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  });

export const MatterLostWageSchema = z
  .object({
    id: z.string(),
    description: z.string().trim().nullable().optional(),
    positionRate: z.coerce.number().nullable().optional(),
    employed: z.boolean().nullable().optional(),
    positionTitle: z.string().trim().nullable().optional(),
    positionType: z.string().trim().nullable().optional(),
    timeMissed: z.coerce.number().nullable().optional(),
  })
  .merge(commonMatterFields);

export const MatterLienSchema = z.object({
  id: z.string(),
  matterId: z.string(),
  firmId: z.string(),
  label: z.string().trim().nullable().optional(),
  description: z
    .string()
    .trim()
    .max(5000, "Description must be 5,000 characters or less")
    .nullable()
    .optional(),
  category: z
    .enum(MATTER_LIEN_CATEGORY_OPTIONS)
    .nullable()
    .optional()
    .or(z.literal("")),
  subCategory: z
    .enum(MATTER_LIEN_SUBCATEGORY_OPTIONS)
    .nullable()
    .optional()
    .or(z.literal("")),
  type: z.string().trim().nullable().optional(),
  status: z.string().trim().nullable().optional(),
  originalAmount: z.coerce
    .number()
    .max(1000000000, {
      message: "Original amount must be 1,000,000,000 or less",
    })
    .nullable()
    .or(z.literal(null))
    .optional(),
  goalAmount: z.coerce
    .number()
    .max(1000000000, {
      message: "Goal amount must be 1,000,000,000 or less",
    })
    .nullable()
    .or(z.literal(null))
    .optional(),
  negotiatedAmount: z.coerce
    .number()
    .max(1000000000, {
      message: "Negotiated amount must be 1,000,000,000 or less",
    })
    .or(z.literal(null))
    .nullable()
    .optional(),
  paidAmount: z.coerce
    .number()
    .max(1000000000, {
      message: "Paid amount must be 1,000,000,000 or less",
    })
    .or(z.literal(null))
    .nullable()
    .optional(),
  reductionFeeAgreement: z.boolean().nullable().optional(),
  dateCreated: z.string().nullable().optional(),
  dateModified: z.string().nullable().optional(),
  connections: ConnectionSchema.array().default([]),
  connectionsNextCursor: z.string().nullable().optional(),
});

export const MatterStatuteOfLimitationsSchema = z
  .object({
    id: z.string(),
    confirmed: z.boolean().optional(),
    date: z.string().refine((value) => value, {
      message: "Statute of Limitations date is required",
    }),
    description: z
      .string()
      .trim()
      .max(5000, "Description must be 5,000 characters or less")
      .optional(),
  })
  .merge(commonMatterFields)
  .extend({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
  });

export const MatterLitigationDateTypeSchema = z
  .object({
    category: z.string(),
    description: z.string().nullable().optional(),
    label: z.string().min(1, { message: "Label is required" }),
  })
  .merge(matterSettingFields);

export const MatterLitigationDateSchema = z
  .object({
    completedDate: z.string().nullable(),
    confirmation: z.boolean().nullable(),
    deadlineDate: z.string().nullable(),
    deadlineTime: z.string().nullable(),
    description: z
      .string()
      .trim()
      .max(5000, "Description must be 5,000 characters or less")
      .nullable(),
    label: z.string().nullable(),
    matterId: z.string(),
    planId: z.string().nullable(),
    status: z.string().nullable(),
    typeCategory: z.string().nullable(),
    typeDescription: z.string().nullable(),
    typeId: z.string(),
  })
  .merge(matterSettingFields);

export const MatterLitigationDatePlanSchema = z
  .object({
    description: z.string().nullable().optional(),
    incidentState: z.array(StateSchema).nullable().optional(),
    label: z.string().min(1, { message: "Label is required" }),
  })
  .merge(matterSettingFields);

export const MatterLitigationDatePlanDateSchema = z
  .object({
    deadline: z.coerce.number(),
    planId: z.string(),
    triggerId: z.string(),
    typeId: z.string(),
  })
  .merge(matterSettingFields);

export const MatterLitigationDatePlanConnectionSchema = z
  .object({
    matterLitigationDatePlanConnectionId: z.string(),
    matterLitigationDatePlanId: z.string(),
    matterLitigationDatePlanConnectionUserInput: z.array(
      z.object({
        matterLitigationDateTypeId: z.string(),
        matterLitigationDateDeadlineDate: z.string(),
      }),
    ),
  })
  .merge(commonMatterFields);

export const MatterPropertyDamageSchema = z.object({
  dateCreated: z.string().nullable().optional(),
  dateModified: z.string().nullable().optional(),
  connections: ConnectionSchema.array().default([]),
  connectionsNextCursor: z.string().nullable().optional(),
  description: z
    .string()
    .trim()
    .max(5000, "Description must be 5,000 characters or less")
    .nullable()
    .optional(),
  firmId: z.string(),
  id: z.string(),
  label: z
    .string()
    .trim()
    .max(50, "Label must be 50 characters or less")
    .nullable()
    .optional(),
  originalAmount: z.coerce
    .number()
    .max(1000000000, {
      message: "Original amount must be 1,000,000,000 or less",
    })
    .nullable()
    .or(z.literal(null))
    .optional(),
  paidAmount: z.coerce
    .number()
    .max(1000000000, {
      message: "Paid amount must be 1,000,000,000 or less",
    })
    .nullable()
    .or(z.literal(null))
    .optional(),
  paymentDate: z.string().nullable().optional(),
  paymentMethod: z.string().nullable().optional(),
  paymentMethodDetails: z
    .string()
    .max(50, "Payment method details must be 50 characters or less")
    .nullable()
    .optional(),
  paymentStatus: z.string().nullable().optional(),
  matterId: z.string(),
});

export const MatterTrustCheckSchema = z
  .object({
    id: z.string(),
    amount: z.coerce
      .number()
      .max(1000000000, {
        message: "Amount must be 1,000,000,000 or less",
      })
      .nullable()
      .or(z.literal(null))
      .optional()
      .refine((value) => value, {
        message: "Amount is required",
      }),
    dateReceived: z.string().refine((value) => value, {
      message: "Date received is required",
    }),
    details: z.string().trim().optional(),
    referenceNumber: z
      .string()
      .trim()
      .min(1, { message: "Reference number is required" })
      .max(100, {
        message: "Reference number must be less than 100 characters",
      }),
    type: z.enum(MATTER_TRUST_CHECK_TYPE_OPTIONS),
  })
  .merge(commonMatterFields);

export const MatterTrustCheckConnectionSchema = z
  .object({
    id: z.string(),
    trustCheckId: z.string(),
    settlementOfferId: z.string().optional(),
    settlementDemandId: z.string().optional(),
    settlementCounterId: z.string().optional(),
    expenseId: z.string().optional(),
    lienId: z.string().optional(),
    medicalTreatmentBillId: z.string().optional(),
  })
  .merge(commonMatterFields);

export const MatterScheduledMessageSchema = z
  .object({
    active: z.boolean(),
    content: z.string().min(1, { message: "Content is required" }),
    description: z.string().nullable().optional(),
    label: z.string().min(1, { message: "Label is required" }),
    s3Keys: z.array(z.string()).nullable().optional(),
    timezone: z.string().nullable().optional(),
    triggerMedicalAppointment: z.string().nullable().optional(),
    triggerStatusId: z.string().nullable().optional(),
    incidentTypeIds: z.array(z.string()).nullable().optional(),
  })
  .merge(matterSettingFields);

export const MatterSettingsSchema = z.object({
  defaultExpenses: z.array(DefaultMatterExpenseSchema),
  defaultFileRepositoryStructures: z.array(
    DefaultMatterFileRepositoryStructureSchema,
  ),
  intakeTemplates: z.array(MatterIntakeTemplateSchema),
  litigationDatePlans: z.array(MatterLitigationDatePlanSchema),
  litigationDateTypes: z.array(MatterLitigationDateTypeSchema),
  roles: z.array(MatterRoleSchema),
  scheduledMessages: z.array(MatterScheduledMessageSchema),
  statuses: z.array(MatterStatusSchema),
  tags: z.array(MatterTagSchema),
  taskPlans: z.array(MatterTaskPlanSchema),
  templates: z.array(MatterTemplateSchema),
  teams: z.array(MatterTeamSchema),
  incidentTypes: z.array(IncidentTypeSchema).optional(),
  incidentTypeCategories: z.array(IncidentTypeCategorySchema).default([]),
  claimTypes: z.array(ClaimTypeSchema).optional(),
});

export const MatterIncidentSchema = z
  .object({
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
    id: z.string(),
    firmId: z.string(),
    street1: z.string().nullish(),
    street2: z.string().nullish(),
    city: z.string().nullish(),
    country: z.string().nullish(),
    county: z.string().nullish(),
    dateOfLoss: z.string().nullish(),
    description: z.string().nullish(),
    incidentTime: z.string().nullish(),
    locationDetails: z.string().nullish(),
    state: z.string().nullish(),
    zipCode: z.string().nullish(),
  })
  .merge(commonMatterFields);

export const MatterRelatedMatterSchema = z
  .object({
    clientId: z.string().nullable().optional(),
    clientName: z.string().nullable().optional(),
    id: z.string(),
    relatedMatterId: z.string(),
    relatedMatterTitle: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
  })
  .merge(commonMatterFields);

export const MatterAiGeneratedMatterItemSchema = z
  .object({
    aiGeneratedMatterItemId: z.string(),
    medicalTreatmentId: z.string().optional(),
  })
  .extend(MedicalTreatmentSchema.shape);

export const MatterMassTortMatterCustomFieldSchema = z
  .object({
    dateCreated: z.string(),
    dateModified: z.string(),
    firmId: z.string(),
    id: z.string(),
    massTortCustomFieldId: z.string(),
    massTortId: z.string(),
    matterId: z.string(),
    value: z.string().nullable(),
    customField: z
      .object({
        dateCreated: z.string(),
        dateModified: z.string(),
        description: z.string().nullable(),
        firmId: z.string(),
        id: z.string(),
        label: z.string(),
        massTortId: z.string(),
        order: z.number(),
        valueOptions: z.array(z.string()).nullable(),
        valueType: z.enum(["select", "text", "date"]),
      })
      .nullable(),
  })
  .passthrough();

export const MatterSchema = z
  .object({
    aiAcknowledgement: z.boolean().nullable().optional(),
    aiAffectedFields: z.unknown().nullable().optional(),
    aiStatus: z.string().nullable().optional(),
    assignees: MatterAssigneeSchema.array().default([]),
    attorneyFee: z.union([z.string(), z.number()]).nullable().optional(),
    clientCommunication: z.boolean().nullable().optional(),
    clientCommunicationDescription: z.string().nullable().optional(),
    clientHealthInsurance: z.boolean().nullable().optional(),
    clientInjuryDescription: z.string().nullable().optional(),
    clientMaritalStatus: z.string().nullable().optional(),
    clientMarried: z.boolean().nullable().optional(),
    clientMinorAtIncident: z.boolean().nullable().optional(),
    clientNoSocialSecurity: z.boolean().nullable().optional(),
    clientPriorClaim: z.boolean().nullable().optional(),
    clientPriorClaimDescription: z.string().nullable().optional(),
    clientPriorInjury: z.boolean().nullable().optional(),
    clientPriorInjuryDescription: z.string().nullable().optional(),
    clientPriorInjuryLegalRepresentation: z.boolean().nullable().optional(),
    clientRentalCar: z.boolean().nullable().optional(),
    clientRentalCarDays: z.number().nullable().optional(),
    clientSoughtTreatment: z.boolean().nullable().optional(),
    clientVisibleBodilyInjury: z.boolean().nullable().optional(),

    criticalInfo: z.string().nullable().optional(),
    dateOfLoss: z.string().nullable().optional(),
    dateRetained: z.string().nullable().optional(),
    deathOccurredFromIncident: z.boolean().nullable().optional(),
    governmentEntityInvolved: z.boolean().nullable().optional(),
    dateModified: z.string(),
    dateCreated: z.string(),
    emailAddress: z.string().nullable().optional(),
    emailAttachments: MatterEmailAttachmentSchema.array().default([]),
    emails: MatterEmailSchema.array().default([]),
    expenses: MatterExpenseSchema.array().default([]),
    filedDate: z.string().nullable().optional(),
    fileReferences: MatterFileReferenceSchema.array().default([]),
    files: MatterFileSchema.array().default([]),
    firmId: z.string().nullable().optional(),
    id: z.string(),
    incidentAddressCity: z.string().nullable().optional(),
    incidentAddressCountry: z.string().nullable().optional(),
    incidentAddressCounty: z.string().nullable().optional(),
    incidentAddressDetails: z.string().nullable().optional(),
    incidentAddressState: z.string().nullable().optional(),
    incidentAddressStreet1: z.string().nullable().optional(),
    incidentAddressStreet2: z.string().nullable().optional(),
    incidentAddressZip: z.string().nullable().optional(),
    incidentDescription: z.string().nullable().optional(),
    incidentDuringScopeOfEmployment: z.boolean().nullable().optional(),
    incidentDuringScopeOfEmploymentTravelingFrom: z
      .string()
      .nullable()
      .optional(),
    incidentDuringScopeOfEmploymentTravelingTo: z
      .string()
      .nullable()
      .optional(),
    incidentInjuryDescription: z.string().nullable().optional(),
    incidentPoliceReport: z.boolean().nullable().optional(),
    incidentPoliceReportNumber: z.string().nullable().optional(),
    incidentReportNotApplicable: z.boolean().nullable().optional(),
    incidentReports: MatterIncidentReportSchema.array().default([]),
    incidentRoadConditions: z.string().nullable().optional(),
    incidentTime: z.string().nullable().optional(),
    incidentWeatherConditions: z.string().nullable().optional(),
    insurances: MatterInsuranceSchema.array().default([]),
    intakeDate: z.string().nullable().optional(),
    intakeTemplateResponses: MatterIntakeTemplateResponseSchema.array().default(
      [],
    ),
    lastSeenAt: z.string().nullable().optional(),
    lastSeenDatetime: z.string().nullable().optional(),
    lastSeenUsername: z.string().nullable().optional(),
    lastTouchDataBeforeAction: z.string().nullable().optional(),
    lastTouchDataCurrent: z.string().nullable().optional(),
    lastTouchDatetime: z.string().nullable().optional(),
    lastTouchMessage: z.string().nullable().optional(),
    lastTouchUsername: z.string().nullable().optional(),
    leadSourceId: z.string().nullable().optional(),
    liens: MatterLienSchema.array().default([]),
    litigationDates: MatterLitigationDateSchema.array().default([]),
    litigationDatesPlanConnection:
      MatterLitigationDatePlanConnectionSchema.array().default([]),
    lostWages: MatterLostWageSchema.array().default([]),
    massTortCustomFields: MatterMassTortMatterCustomFieldSchema.array().default(
      [],
    ),
    massTortId: z.string().nullable().optional(),
    medicalTreatmentAppointmentConnections:
      MedicalTreatmentAppointmentConnectionSchema.array().default([]),
    medicalTreatmentAppointments:
      MedicalTreatmentAppointmentSchema.array().default([]),
    medicalTreatmentBalanceConfirmations:
      MedicalTreatmentBalanceConfirmationSchema.array().default([]),
    medicalTreatmentBills: MedicalTreatmentBillSchema.array().default([]),
    medicalTreatmentPlans: MedicalTreatmentPlanSchema.array().default([]),
    medicalTreatmentRecords: MedicalTreatmentRecordSchema.array().default([]),
    medicalTreatments: MedicalTreatmentSchema.array().default([]),
    noteComments: MatterNoteCommentSchema.array().default([]),
    propertyDamages: MatterPropertyDamageSchema.array().default([]),
    incident: MatterIncidentSchema.nullish(),
    relatedMatterConnections: MatterRelatedMatterSchema.array().default([]),
    settlementCounters: MatterSettlementCounterSchema.array().default([]),
    settlementDemandPackageSegments:
      MatterSettlementDemandPackageSegmentSchema.array().default([]),
    settlementDemands: MatterSettlementDemandSchema.array().default([]),
    settlementOffers: MatterSettlementOfferSchema.array().default([]),
    shareFolderFiles: MatterShareFolderFileSchema.array().default([]),
    shareFolders: MatterShareFolderSchema.array().default([]),
    signatureRequests: MatterSignatureRequestSchema.array().default([]),
    status: MatterStatusSchema,
    statusId: z.string(),
    statusPhases: MatterStatusPhaseSchema.array().default([]),
    statuteOfLimitations: z.string().nullable().optional(),
    statuteOfLimitationsConfirmed: z.boolean().nullable().optional(),
    statutesOfLimitations: MatterStatuteOfLimitationsSchema.array().default([]),
    summary: z.string().nullable().optional(),
    tagConnections: MatterTagConnectionSchema.array().default([]),
    teamConnections: MatterTeamConnectionSchema.array().default([]),
    title: z.string(),
    trustCheckConnections: MatterTrustCheckConnectionSchema.array().default([]),
    trustChecks: MatterTrustCheckSchema.array().default([]),
    incidentType: z
      .object({
        emailModuleAccess: z.boolean().nullable().optional(),
        expenseModuleAccess: z.boolean().nullable().optional(),
        incidentModuleAccess: z.boolean().nullable().optional(),
        incidentReportModuleAccess: z.boolean().nullable().optional(),
        injuriesModuleAccess: z.boolean().nullable().optional(),
        insuranceModuleAccess: z.boolean().nullable().optional(),
        intakePropertyDamageModuleAccess: z.boolean().nullable().optional(),
        intakeResponsesModuleAccess: z.boolean().nullable().optional(),
        lienModuleAccess: z.boolean().nullable().optional(),
        litigationModuleAccess: z.boolean().nullable().optional(),
        lostWagesModuleAccess: z.boolean().nullable().optional(),
        medicalModuleAccess: z.boolean().nullable().optional(),
        medicalTreatmentModuleAccess: z.boolean().nullable().optional(),
        settlementModuleAccess: z.boolean().nullable().optional(),
        signatureRequestModuleAccess: z.boolean().nullable().optional(),
        trustCheckModuleAccess: z.boolean().nullable().optional(),
        vehicleModuleAccess: z.boolean().nullable().optional(),
        id: z.string(),
        label: z.string().nullish(),
        incidentTypeCategoryId: z.string().nullish(),
        category: IncidentTypeCategorySchema.nullish(),
      })
      .nullish(),
    incidentTypeId: z.string().nullish(),
    claimTypeIds: z.array(z.string()).default([]),
    claimTypes: z.array(ClaimTypeSchema).optional(),
    userConnections: MatterUserConnectionSchema.array().default([]),
    connections: ConnectionSchema.array().default([]),
    connectionsNextCursor: z.string().nullable().optional(),
    vehicles: MatterVehicleSchema.array().default([]),
    pinnedMatters: PinnedMatterSchema.array().default([]),
  })
  .passthrough();

export const MatterV3Schema = MatterSchema;
